import type { HTMLAttributes, KeyboardEvent, ReactNode, SelectHTMLAttributes } from 'react';
import get from 'lodash/get';
import { useFormikContext } from 'formik';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DropdownMenu, { type DropdownMenuProps } from '@tmh-website/ui/DropdownMenu';
import DropdownMenuItem from '@tmh-website/ui/DropdownMenuItem';
import FormErrorMessage from '@tmh-website/components/shared/FormErrorMessage';
import LoadingSpinner from '@tmh-website/ui/LoadingSpinner';
import type { FormElementCommonProps, SelectOption, SelectSearch } from '@tmh-website/ui/FormElements';
import { Color } from '@tmh-website/shared/colors';
import { formElementThemes, StyledFormElements } from '@tmh-website/ui/FormElements';
import { searchSelectOptions } from '@tmh-website/services/search.service';

import * as Styled from './Select.styles';

export interface SelectProps
  extends FormElementCommonProps,
    Partial<Omit<SelectHTMLAttributes<HTMLSelectElement>, 'name'>> {
  dropdownMenuContainerProps?: Record<string, any>;

  dropdownMenuVirtualScrollContainerProps?: HTMLAttributes<HTMLDivElement> & {
    [key: `data-${string}`]: string;
  };

  helpText?: string | ReactNode;

  /**
   * Set this to true to use this field without a label
   */
  hideLabel?: boolean;

  isLoading?: boolean;
  options: SelectOption[];
  placeholder?: ReactNode;
  readOnly?: boolean;
  search?: SelectSearch;

  /**
   * The label usually is only small when a value is selected. Set this to true to always force the label to be small.
   */
  smallLabel?: boolean;
}

const Select = ({
  disabled,
  dropdownMenuContainerProps = {},
  dropdownMenuVirtualScrollContainerProps = {},
  helpText,
  hideLabel = false,
  isLoading = false,
  label,
  name,
  options,
  placeholder,
  readOnly,
  required = false,
  search = false,
  smallLabel = false,
  themeColor = Color.Neutral_0_White,
  value,

  // TODO: These `props` are being passed to the `<StyledFormElements.Container>` which is a `<div>`,
  //   so not all the properties have the same types as the `SelectProps` interface defines it
  //   (because the `SelectProps` extends the `SelectHTMLAttributes<HTMLSelectElement>`).
  //   We could try to define those types a bit better, maybe by separating the `<select>` attributes from the `<div>`
  //   attributes, using `selectAttrs` and `divAttrs` properties.
  ...props
}: SelectProps) => {
  const { t } = useTranslation();
  const uniqueId = useId();
  const nameWithId = name + '_' + uniqueId;
  const { errors, touched, values, setFieldValue, setFieldTouched } = useFormikContext<any>();
  const hasError = get(errors, name) && get(touched, name);
  const formikValue = get(values, name);
  const [isVisible, setIsVisible] = useState(false);
  const [activeSearchFilter, setActiveSearchFilter] = useState('');
  const hasLabelForFalsyValue = options.some(option => !option.value);
  const showPlaceholder = !value && !!placeholder;

  let filteredOptions = options;

  if (search && activeSearchFilter.trim() !== '') {
    if (typeof search === 'function') {
      filteredOptions = search(options, activeSearchFilter);
    } else {
      filteredOptions = searchSelectOptions(options, activeSearchFilter, search);
    }
  }

  const visibleLabel = isLoading
    ? undefined
    : showPlaceholder
    ? placeholder
    : filteredOptions.find(option => option.value === value)?.label;
  /**
   * Handles the `onOpenChange` event on the `<DropdownMenu>`.
   *
   * @param newValue - Determines whether the dropdown menu is currently open.
   */
  const handleDropdownMenuOpenChange: DropdownMenuProps['onOpenChange'] = newValue => {
    if (!newValue) setFieldTouched(name);
  };

  return (
    <div>
      <StyledFormElements.Container
        $disableSmallLabelOnFocus
        $hasError={hasError}
        $isDisabled={disabled}
        $isReadOnly={readOnly}
        $themeColor={themeColor}
        {...(props as unknown as HTMLAttributes<HTMLDivElement>)}>
        {label && !hideLabel && (
          <StyledFormElements.InputLabel
            $themeColor={themeColor}
            $fieldIsFilled={
              (formikValue !== '' && formikValue !== undefined && formikValue !== null) ||
              smallLabel ||
              showPlaceholder ||
              hasLabelForFalsyValue
            }
            htmlFor={nameWithId}>
            {label}
            {required && '*'}
          </StyledFormElements.InputLabel>
        )}
        <DropdownMenu
          customContainerProps={{ id: nameWithId }}
          customOffset={6}
          disabled={disabled || readOnly}
          hasSearchInput={search !== false}
          onOpenChange={handleDropdownMenuOpenChange}
          onSearchInputChange={setActiveSearchFilter}
          themeColor={formElementThemes[themeColor].dropdownThemeColor}
          virtualScrollContainerProps={dropdownMenuVirtualScrollContainerProps}
          anchorEl={
            <Styled.SelectButton
              $themeColor={themeColor}
              aria-label={visibleLabel ? undefined : t('global:pleaseChoose')}
              as='button'
              disabled={disabled}
              name={name}
              onClick={() => {
                setIsVisible(!isVisible);
              }}
              onKeyUp={(e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                  setIsVisible(!isVisible);
                }
              }}
              type='button'>
              <Styled.FakeValue $themeColor={themeColor} $isPlaceholder={!!showPlaceholder}>
                {visibleLabel}
                {isLoading && <LoadingSpinner color={formElementThemes[themeColor].text} absolutePosition />}
              </Styled.FakeValue>
            </Styled.SelectButton>
          }>
          {filteredOptions.map(option => (
            <DropdownMenuItem
              key={option.label}
              themeColor={formElementThemes[themeColor].dropdownThemeColor}
              disabled={option.disabled}
              onSelect={async () => {
                await setFieldValue(name, option.value);
                await setFieldTouched(name, true, true);
              }}
              onBlur={() => {}}>
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
      </StyledFormElements.Container>
      {helpText && <StyledFormElements.HelpText $themeColor={themeColor}>{helpText}</StyledFormElements.HelpText>}
      {hasError && (
        <StyledFormElements.ErrorMessage>
          <FormErrorMessage name={name} />
        </StyledFormElements.ErrorMessage>
      )}
    </div>
  );
};

export default Select;
